<script lang="ts">
  import { onMount } from 'svelte';

  type Props = {
    src: string;
    toleranceThreshold?: number;
    padding?: number;
    backgroundColor?: string;
    transparent?: boolean;
    [key: string]: any; // Allow any additional props to pass through to img
  };

  const props: Props = $props();
  const {
    src,
    toleranceThreshold = 2,
    padding = 8,
    backgroundColor = "white",
    transparent = false,
    ...restProps
  } = props;

  let canvas: HTMLCanvasElement;
  let croppedImageSrc = $state<string>(`data:image/svg+xml;utf8,<svg version="1.1" width="1" height="1" xmlns="http://www.w3.org/2000/svg"></svg>`);

  const processImage = async () => {
    if (!canvas || !src) return;

    const originalImage = new Image();
    originalImage.crossOrigin = "anonymous";

    try {
      await new Promise((resolve, reject) => {
        originalImage.onload = resolve;
        originalImage.onerror = reject;
        originalImage.src = src;
      });

      const ctx = canvas.getContext('2d')!;
      const width = canvas.width = originalImage.width;
      const height = canvas.height = originalImage.height;
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, width, height);

      if (transparent) {
        ctx.drawImage(originalImage, 0, 0);
        removeBackground(ctx, width, height);
      } else {
        const colorToTrim = ctx.getImageData(0, 0, 1, 1).data.slice(0, 3);
        ctx.drawImage(originalImage, 0, 0);
        cropToSubject(ctx, originalImage, colorToTrim, width, height);
      }

      const blob = await new Promise<Blob>((resolve, _)=>canvas.toBlob(resolve));
      croppedImageSrc = URL.createObjectURL(blob);
    } catch (error) {
      console.error("Error processing image:", error);
    }
  };

  // BFS flood fill from white background to handle light-colored subject
  // regions that a naive per-pixel approach would incorrectly erase.
  // Alpha formula: alphaInt = 255 - min(r,g,b)
  // Decontaminated: original = (observed - min) * 255 / alphaInt
  function removeBackground(ctx: CanvasRenderingContext2D, width: number, height: number) {
    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    const cx = width / 2, cy = height / 2;
    const offX = [-1, 1, 0, 0], offY = [0, 0, -1, 1];

    const alphaOf = (pi: number) => 255 - Math.min(data[pi], data[pi + 1], data[pi + 2]);

    // Sample on a 4×4 grid: white pixels become flood-fill seeds,
    // non-white pixels establish the bounding box
    let left = width, right = 0, top = height, bottom = 0;
    const starts: [number, number, number][] = [];
    for (let sy = 0; sy < height; sy += 4) {
      for (let sx = 0; sx < width; sx += 4) {
        if (alphaOf((sy * width + sx) * 4) < toleranceThreshold) {
          const dx = sx - cx, dy = sy - cy;
          starts.push([dx * dx + dy * dy, sx, sy]);
        } else {
          if (sx < left) left = sx;
          if (sx > right) right = sx;
          if (sy < top) top = sy;
          if (sy > bottom) bottom = sy;
        }
      }
    }
    starts.sort((a, b) => a[0] - b[0]);

    // Apply padding and clamp — flood fill only within the crop region
    left = Math.max(0, left - padding);
    right = Math.min(width - 1, right + padding);
    top = Math.max(0, top - padding);
    bottom = Math.min(height - 1, bottom + padding);

    // BFS queue: flat array of [x, y, alpha] triples
    const reached = new Uint8Array(width * height);
    const queue: number[] = [];
    let qi = 0;

    for (const [, sx, sy] of starts) {
      if (sx < left || sx > right || sy < top || sy > bottom) continue;
      const si = sy * width + sx;
      reached[si] = 1;
      queue.push(sx, sy, 0);
    }

    while (qi < queue.length) {
      const px = queue[qi], py = queue[qi + 1], pa = queue[qi + 2];
      qi += 3;
      const i = (py * width + px) * 4;

      if (pa < 2) {
        data[i] = data[i + 1] = data[i + 2] = data[i + 3] = 0;
      } else {
        const r = data[i], g = data[i + 1], b = data[i + 2];
        const min = 255 - pa;
        const scale = 255 / pa;
        data[i]     = Math.min(255, ((r - min) * scale + 0.5) | 0);
        data[i + 1] = Math.min(255, ((g - min) * scale + 0.5) | 0);
        data[i + 2] = Math.min(255, ((b - min) * scale + 0.5) | 0);
        data[i + 3] = pa;
      }

      for (let d = 0; d < 4; d++) {
        const nx = px + offX[d], ny = py + offY[d];
        if (nx < left || nx > right || ny < top || ny > bottom) continue;
        const ni = ny * width + nx;
        if (reached[ni]) continue;
        const na = alphaOf(ni * 4);
        if (na < pa || na === 255) continue;
        reached[ni] = 1;
        queue.push(nx, ny, na);
      }
    }

    ctx.putImageData(imageData, 0, 0);
    const cw = right - left + 1, ch = bottom - top + 1;
    const cropped = ctx.getImageData(left, top, cw, ch);
    ctx.canvas.width = cw;
    ctx.canvas.height = ch;
    ctx.putImageData(cropped, 0, 0);
  }

  function cropToSubject(ctx: CanvasRenderingContext2D, originalImage: HTMLImageElement, colorToTrim: Uint8ClampedArray, width: number, height: number) {
    const data = ctx.getImageData(0, 0, width, height).data;
    let left = width, right = 0, top = height, bottom = 0;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4;
        if (Math.max(Math.abs(data[i] - colorToTrim[0]), Math.abs(data[i + 1] - colorToTrim[1]), Math.abs(data[i + 2] - colorToTrim[2])) > toleranceThreshold) {
          if (x < left) left = x;
          if (x > right) right = x;
          if (y < top) top = y;
          if (y > bottom) bottom = y;
        }
      }
    }

    left = Math.max(0, left - padding);
    right = Math.min(width - 1, right + padding);
    top = Math.max(0, top - padding);
    bottom = Math.min(height - 1, bottom + padding);

    const cw = right - left + 1, ch = bottom - top + 1;
    ctx.canvas.width = cw;
    ctx.canvas.height = ch;
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, cw, ch);
    ctx.drawImage(originalImage, left, top, cw, ch, 0, 0, cw, ch);
  }

  onMount(() => {
    if (src) {
      processImage();
      return ()=>URL.revokeObjectURL(croppedImageSrc);
    }
  });
</script>

<canvas bind:this={canvas} style="display: none;"></canvas>
<div style="--cropped: url(&quot;{croppedImageSrc}&quot;)"><img
  {src}
  {...restProps}
  crossorigin="anonymous"
/></div>

<style>
  div {
    display: contents;
  }
  img {
    object-position: 2000px 2000px;
    background-image: var(--cropped);
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
  }
</style>
